/**
 * Mixpanel tracking provider
 */

import type { OverridedMixpanel } from 'mixpanel-browser'
import type {
  TrackingProvider,
  UserProperties,
  GroupProperties,
} from '../types'

const MIXPANEL_TOKEN = '3632572468d682aa08f60180c7641379'

let mixpanel: OverridedMixpanel | null = null
let initPromise: Promise<void> | null = null

export function createMixpanelProvider(): TrackingProvider {
  async function ensureInit(): Promise<OverridedMixpanel> {
    if (mixpanel) return mixpanel

    if (!initPromise) {
      initPromise = (async () => {
        const { default: mp } = await import('mixpanel-browser')
        mp.init(MIXPANEL_TOKEN, {
          autocapture: {
            pageview: false,
            click: true,
          },
          record_sessions_percent: 100,
        })
        mixpanel = mp
      })()
    }

    await initPromise
    return mixpanel!
  }

  return {
    name: 'mixpanel',

    async init(): Promise<void> {
      await ensureInit()
    },

    async reset(): Promise<void> {
      const mp = await ensureInit()
      mp.reset?.()
    },

    async track(
      eventName: string,
      properties: Record<string, unknown>
    ): Promise<void> {
      const mp = await ensureInit()
      mp.track(eventName, properties)
    },

    async identify(userId: string, properties?: UserProperties): Promise<void> {
      const mp = await ensureInit()
      mp.identify(userId)

      const userProps: Record<string, string> = { user_id: userId }
      if (properties?.name) {
        userProps.$name = properties.name
        userProps.user_name = properties.name
      }
      if (properties?.email) {
        userProps.$email = properties.email
        userProps.user_email = properties.email
      }

      mp.people?.set?.(userProps)
      mp.register?.(userProps)
    },

    async setGroup(
      groupKey: string,
      groupId: string | string[]
    ): Promise<void> {
      const mp = await ensureInit()
      const id = Array.isArray(groupId) ? groupId[0] : groupId
      if (id) {
        mp.set_group?.(groupKey, id)
      }
    },

    async identifyGroup(
      groupKey: string,
      groupId: string,
      properties?: GroupProperties
    ): Promise<void> {
      const mp = await ensureInit()

      const groupProps: Record<string, string> = {
        [`${groupKey}_id`]: groupId,
      }
      if (properties?.name) {
        groupProps[`${groupKey}_name`] = properties.name
        groupProps.name = properties.name
      }

      mp.get_group?.(groupKey, groupId)?.set?.(groupProps)
      mp.people?.set?.(groupProps)
      mp.register?.(groupProps)
    },

    async clearGroup(groupKey: string): Promise<void> {
      const mp = await ensureInit()
      mp.unregister?.(`${groupKey}_id`)
    },
  }
}
